import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { FaPhone, FaEnvelope, FaClock, FaMapMarkerAlt, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { useMobileDetector } from '../utils/deviceDetection';

const ContactCard = ({ icon, title, content }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-6 rounded-lg flex items-start space-x-4 transform hover:scale-105 transition duration-200 border border-gray-700 shadow-lg hover:shadow-xl">
    <div className="text-blue-400 text-2xl">{icon}</div>
    <div>
      <h3 className="font-semibold mb-2 text-white">{title}</h3>
      <p className="text-blue-100">{content}</p>
    </div>
  </div>
);

// Mobile contact quick action button
const MobileContactAction = ({ icon, title, action, href }) => (
  <a 
    href={href}
    onClick={action} 
    className="bg-gradient-to-br from-surface to-gray-800 p-4 rounded-lg flex items-center justify-between border border-gray-700 shadow-lg mb-3"
  >
    <div className="flex items-center">
      <div className="text-blue-400 text-xl mr-3">{icon}</div>
      <span className="font-semibold text-white">{title}</span>
    </div>
    <FaArrowRight className="text-blue-400" />
  </a>
);

const Contact = () => {
  const isMobile = useMobileDetector();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [formStep, setFormStep] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');

    try {
      // Send email to support@autolawn.app
      await axiosInstance.post('/contact/send-email', {
        ...formData,
        to: 'support@autolawn.app' // Explicitly set the recipient to support@autolawn.app
      });
      
      setStatus('sent');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setFormStep(0);
    } catch (err) {
      console.error('Contact form submission error:', err);
      setError('Failed to send message. Please try again or email us directly at support@autolawn.app.');
      setStatus('error');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    setFormStep(formStep + 1);
  };

  const prevStep = () => {
    setFormStep(formStep - 1);
  };

  // Validate current step before proceeding
  const canProceed = () => {
    if (formStep === 0) {
      return formData.name.trim() !== '';
    } else if (formStep === 1) {
      return formData.email.trim() !== '' && formData.email.includes('@');
    } else if (formStep === 2) {
      return formData.subject.trim() !== '';
    }
    return true;
  };

  // Different form sections based on step (for mobile)
  const renderFormStep = () => {
    switch (formStep) {
      case 0:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-3 text-white">What's your name?</h3>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
              placeholder="Your name"
              required
            />
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="px-4 py-2 rounded-md bg-blue-500 text-white font-medium disabled:opacity-50 flex items-center"
              >
                Next <FaArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        );
      case 1:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-3 text-white">What's your email?</h3>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
              placeholder="your.email@example.com"
              required
            />
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 rounded-md bg-gray-700 text-white font-medium flex items-center"
              >
                <FaArrowLeft className="mr-2" /> Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="px-4 py-2 rounded-md bg-blue-500 text-white font-medium disabled:opacity-50 flex items-center"
              >
                Next <FaArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-3 text-white">What's your inquiry about?</h3>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
              placeholder="Subject of your message"
              required
            />
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 rounded-md bg-gray-700 text-white font-medium flex items-center"
              >
                <FaArrowLeft className="mr-2" /> Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                className="px-4 py-2 rounded-md bg-blue-500 text-white font-medium disabled:opacity-50 flex items-center"
              >
                Next <FaArrowRight className="ml-2" />
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h3 className="text-lg font-semibold mb-3 text-white">Your message</h3>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
              placeholder="How can we help you?"
              required
            ></textarea>
            <div className="flex justify-between mt-4">
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 rounded-md bg-gray-700 text-white font-medium flex items-center"
              >
                <FaArrowLeft className="mr-2" /> Back
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md bg-blue-500 text-white font-medium disabled:opacity-50"
                disabled={status === 'sending'}
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16 relative">
          <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
          <h1 className="relative text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Get in Touch
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto relative">
            Have questions about AUTOLAWN? We're here to help! Reach out to our team 
            and we'll get back to you as soon as possible.
          </p>
        </div>

        {isMobile ? (
          // Mobile: Quick action buttons
          <div className="mb-8">
            <MobileContactAction 
              icon={<FaPhone />}
              title="Call Us"
              href="tel:+17249143764"
            />
            <MobileContactAction 
              icon={<FaEnvelope />}
              title="Email Us"
              href="mailto:support@autolawn.app"
            />
            <div className="bg-gradient-to-br from-surface to-gray-800 p-4 rounded-lg border border-gray-700 shadow-lg mb-3">
              <div className="flex items-center text-blue-400 text-xl mb-2">
                <FaClock className="mr-3" />
                <span className="font-semibold text-white">Business Hours</span>
              </div>
              <p className="text-blue-100 pl-8">Mon-Fri: 9AM - 5PM EST</p>
            </div>
          </div>
        ) : (
          // Desktop: Contact Info Cards in grid
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <ContactCard
              icon={<FaPhone />}
              title="Phone"
              content="(724) 914-3764"
            />
            <ContactCard
              icon={<FaEnvelope />}
              title="Email"
              content="support@autolawn.app"
            />
            <ContactCard
              icon={<FaClock />}
              title="Hours"
              content="Mon-Fri: 9AM - 5PM EST"
            />
            <ContactCard
              icon={<FaMapMarkerAlt />}
              title="Location"
              content="USA"
            />
          </div>
        )}

        {/* Contact Form */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-surface to-gray-800 rounded-lg p-6 md:p-8 border border-gray-700 shadow-lg">
            <h2 className="text-xl md:text-2xl font-bold mb-6 text-white">Send us a Message</h2>
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-md mb-6">
                {error}
              </div>
            )}
            {status === 'sent' && (
              <div className="bg-green-500/10 border border-green-500 text-green-500 p-4 rounded-md mb-6">
                Message sent successfully! We'll get back to you soon.
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {isMobile ? (
                // Mobile: Step-by-step form
                renderFormStep()
              ) : (
                // Desktop: Full form
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-blue-100">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-blue-100">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-100">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-100">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      className="w-full p-3 rounded-md bg-white/10 border border-gray-600 focus:border-blue-400 focus:ring focus:ring-blue-400/50 text-white placeholder-gray-400"
                      required
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-md font-medium hover:from-blue-600 hover:to-blue-700 transition duration-200 shadow-lg hover:shadow-blue-500/50 disabled:opacity-50"
                    disabled={status === 'sending'}
                  >
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 md:mt-16 text-center">
          <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Frequently Asked Questions</h2>
          <p className="text-gray-300 mb-4">
            Can't find what you're looking for? Check out our
            <Link to="/faq" className="text-blue-400 hover:text-blue-300 ml-2">
              FAQ page
            </Link>
            {' '}or our{' '}
            <Link to="/training" className="text-blue-400 hover:text-blue-300">
              Training Resources
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default Contact;
